import { at, numbers, PAGE_SIZES } from '../paging';

/**
 * THE WAY THROUGH A LONG LIST, drawn once and used by every list that has one.
 *
 * ONE COMPONENT RATHER THAN THREE, and that is the requirement rather than
 * tidiness. Vinnie, 30 August 2026: **"pages on both, keep it consistent."**
 * Two screens that each grew their own idea of what a cap means is exactly how
 * [[#294]] and [[#296]] ended up being two cards.
 *
 * SPLIT IN TWO, because the two halves belong in different places on the page.
 * The size sits with the other filters at the top, where somebody decides what
 * they are looking at; the numbers sit under the list, where they have finished
 * reading it. A pager drawn whole in one place would have to be in the wrong
 * one of those.
 */

/** The size picker, for the filter row at the top. */
export function PageSize({ size, onSize }: { size: number; onSize: (n: number) => void }) {
  return (
    <label className="pages-size">
      Show
      <select aria-label="Rows per page" value={size} onChange={(e) => onSize(Number(e.target.value))}>
        {PAGE_SIZES.map((n) => (
          <option key={n} value={n}>
            {n}
          </option>
        ))}
      </select>
      per page
    </label>
  );
}

/**
 * The numbers, for under the list.
 *
 * DRAWS NOTHING WHEN THERE IS ONE PAGE. A row of controls saying "1" is a
 * control that does nothing, and a screen full of those teaches people that the
 * controls on it do nothing.
 */
export function Pages({
  total,
  size,
  page,
  onPage,
  what = 'entries',
}: {
  total: number;
  size: number;
  page: number;
  onPage: (n: number) => void;
  /** Plural noun for the sentence — "entries", "changes". */
  what?: string;
}) {
  const p = at(total, size, page);
  if (p.pages <= 1) return null;
  return (
    <nav className="pages" aria-label={`Pages of ${what}`}>
      {/* THE SENTENCE FIRST, and it is not decoration: it is the count #294
          was raised about, kept in view wherever the reader has got to. */}
      <span className="pages-said">
        {p.from}–{p.to} of {total} {what}
      </span>
      <div className="pages-numbers">
        <button className="pages-step" disabled={p.page === 1} onClick={() => onPage(p.page - 1)}>
          Back
        </button>
        {numbers(p.page, p.pages).map((n, i) =>
          n === null ? (
            /* A gap is not a control and must not be reachable by keyboard. */
            <span key={`gap${i}`} className="pages-gap" aria-hidden="true">
              …
            </span>
          ) : (
            <button
              key={n}
              className={`pages-n${n === p.page ? ' pages-here' : ''}`}
              aria-current={n === p.page ? 'page' : undefined}
              onClick={() => onPage(n)}
            >
              {n}
            </button>
          )
        )}
        <button className="pages-step" disabled={p.page === p.pages} onClick={() => onPage(p.page + 1)}>
          Next
        </button>
      </div>
    </nav>
  );
}
